import React, { useState } from 'react';
import { Share2, Copy, Check, ExternalLink, Eye } from 'lucide-react';
import { theme } from '../../../theme';
import { apiRequest } from '../../../utils/apiUtils';

interface ShareLinkData {
  slug: string;
  complexSlug: string;
  path: string;
  linkViews: number;
}

interface ShareLinkButtonProps {
  venueId: string;
  venueName: string;
}

const ShareLinkButton: React.FC<ShareLinkButtonProps> = ({ venueId, venueName }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkData, setLinkData] = useState<ShareLinkData | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleShare = async () => {
    setShowModal(true);
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest<ShareLinkData>(
        `/owner/venues/${venueId}/share-link`,
        { method: 'POST' }
      );

      if (response.data) {
        setLinkData(response.data);
      } else {
        setError(response.error || 'Error al generar el link');
      }
    } catch {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const fullUrl = linkData
    ? `${window.location.origin}/reservar/${linkData.complexSlug}/${linkData.slug}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setLinkData(null);
    setCopied(false);
    setError(null);
  };

  return (
    <>
      <button
        onClick={handleShare}
        style={{
          padding: '0 24px',
          height: '44px',
          borderRadius: '12px',
          backgroundColor: 'transparent',
          border: `2px solid ${theme.colors.primary}`,
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          fontWeight: '600',
          color: theme.colors.primary,
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <Share2 size={16} />
        Compartir
      </button>

      {/* Modal overlay */}
      {showModal && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '18px',
              border: `1px solid ${theme.colors.border}`,
              padding: '32px',
              width: '480px',
              maxWidth: '90vw',
            }}
          >
            {/* Modal header */}
            <h3 style={{
              fontFamily: theme.fonts.heading,
              fontSize: '28px',
              color: theme.colors.text,
              margin: '0 0 8px 0',
              letterSpacing: '1px',
            }}>
              COMPARTIR CANCHA
            </h3>
            <p style={{
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              color: theme.colors.textSecondary,
              margin: '0 0 24px 0',
            }}>
              {venueName}
            </p>

            {loading && (
              <div style={{
                textAlign: 'center',
                padding: '32px 0',
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.textSecondary,
              }}>
                Generando link...
              </div>
            )}

            {error && (
              <div style={{
                textAlign: 'center',
                padding: '24px 0',
                fontFamily: theme.fonts.body,
                fontSize: '14px',
                color: theme.colors.danger,
              }}>
                {error}
              </div>
            )}

            {linkData && (
              <>
                {/* Link display + copy */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '16px',
                }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: theme.colors.background,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '10px',
                    padding: '12px 16px',
                    fontFamily: theme.fonts.body,
                    fontSize: '13px',
                    color: theme.colors.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {fullUrl}
                  </div>
                  <button
                    onClick={handleCopy}
                    style={{
                      padding: '12px 20px',
                      borderRadius: '10px',
                      backgroundColor: copied ? theme.colors.success : theme.colors.primary,
                      border: 'none',
                      fontFamily: theme.fonts.body,
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#000',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'background-color 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>

                {/* Stats */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  backgroundColor: theme.colors.background,
                  borderRadius: '10px',
                  marginBottom: '24px',
                }}>
                  <Eye size={16} color={theme.colors.textSecondary} />
                  <span style={{
                    fontFamily: theme.fonts.body,
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                  }}>
                    {linkData.linkViews} {linkData.linkViews === 1 ? 'vista' : 'vistas'} desde este link
                  </span>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => window.open(fullUrl, '_blank')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '10px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.colors.border}`,
                      fontFamily: theme.fonts.body,
                      fontSize: '13px',
                      color: theme.colors.textSecondary,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <ExternalLink size={14} />
                    Abrir link
                  </button>
                  <button
                    onClick={handleClose}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '10px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${theme.colors.border}`,
                      fontFamily: theme.fonts.body,
                      fontSize: '13px',
                      color: theme.colors.textSecondary,
                      cursor: 'pointer',
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ShareLinkButton;
